/* eslint-disable react-hooks/exhaustive-deps */
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from "react";

import { i18n } from "@lingui/core";
import {
  type AxiosInstances,
  AuthContext,
  type AuthContextType,
  type AuthorizationSession,
  type CognitoUserSession,
  useSignout,
  type RemoteAuthSession,
  useSession,
  getOpaqueAuthorization,
  invalidateSession,
  setPatAuthorization,
  markAuthRefresh,
  clearCognitoLocalStorage,
  refreshSessionBearer,
  createCatch401ErrorInterceptor,
} from "@mobsuccess-devops/react-shared/auth";
import { axiosPost } from "@mobsuccess-devops/react-shared/fetch";
import { useToasters } from "@mobsuccess-devops/react-ui/Toaster/use-toasters";
import { useQueryClient } from "@tanstack/react-query";

import { signIn as cognitoSignin, confirmSignIn } from "aws-amplify/auth";
import { jwtDecode } from "jwt-decode";

import { useForgotPassword } from "@/api/use-forgot-password";
import { useResetPassword } from "@/api/use-reset-password";
import { useSend2faAuth } from "@/api/use-send-2fa-auth";
import {
  LoginContext,
  type LoginContextType,
} from "@/features/auth/login-context";
import { signInMfa } from "@/features/auth/sign-in-mfa";
import {
  PasskeyNotRegisteredError,
  PasskeySignInAbortedError,
  signInWithPasskey as completePasskeySignIn,
} from "@/features/auth/sign-in-passkey";
import { setLastLoginEmail } from "@/features/passkeys/passkey-local-hint";

type AuthProviderProps = {
  children: JSX.Element;
  googleSignupUrl?: URL;
  googleClientId?: string;
  userPoolsId: string;
  userPoolsWebClientId: string;
  axiosInstances: AxiosInstances;
};

function AuthProvider({
  children,
  axiosInstances,
  googleClientId,
  googleSignupUrl,
  userPoolsId,
  userPoolsWebClientId,
}: AuthProviderProps): JSX.Element | null {
  const { toast } = useToasters();
  const [loginStatus, setLoginStatus] = useState<"idle" | "loading">("idle");
  const [authorization, setAuthorization] = useState(getOpaqueAuthorization());
  const [profile, setProfile] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [trustDevice, setTrustDevice] = useState<boolean>(false);
  const { mutateAsync: send2faAuth } = useSend2faAuth();

  const { mutateAsync: signOutAsync, mutate: signOut } = useSignout({
    postSignout(options) {
      setPatAuthorization(null);
      setAuthorization(getOpaqueAuthorization());
      if (options?.redirect) {
        window.location.assign(options.redirect);
      }
    },
  });

  const {
    isLoading,
    isFetching,
    data: sessionCognito,
  } = useSession({
    queryClient,
    axiosInstances,
    userPoolsWebClientId,
    trustDevice,
  });

  const sessionAuthorization: AuthorizationSession | null = authorization
    ? { authorization: authorization, profile: profile || undefined }
    : null;
  const session:
    CognitoUserSession | RemoteAuthSession | AuthorizationSession | null =
    sessionAuthorization || sessionCognito || null;

  const jwt = useMemo(() => {
    const session = sessionCognito;
    if (!session || !session.tokens || !session.tokens.accessToken) {
      return null;
    }
    const accessToken = session.tokens.accessToken
      .toString()
      .replace(/^jwt:/, "");
    return `Bearer jwt:${accessToken}`;
  }, [sessionCognito]);

  const googleSignIn = useCallback<LoginContextType["googleSignIn"]>(
    async ({ credential }) => {
      try {
        setLoginStatus("loading");

        const decoded = jwtDecode(credential);
        if (!isGoogleTokenPayload(decoded)) {
          throw new Error("Invalid token format");
        }

        // Reset the Cognito storage so that the Google sign-in overwrites whatever is there.
        // This is necessary as Cognito will refuse to “overwrite” a session it thinks is still valid.
        clearCognitoLocalStorage();

        const { email } = decoded;

        await cognitoSignin({
          username: email,
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });
        const answer = await confirmSignIn({
          challengeResponse: credential,
          options: {
            clientMetadata: { authType: "GOOGLE" },
          },
        });
        if (!answer.isSignedIn) {
          throw new Error("Something went wrong while signing in");
        }
        setLastLoginEmail(email);
      } catch (error) {
        signOut({});
        if (
          error instanceof Error &&
          error.name === "UserNotFoundException" &&
          googleSignupUrl
        ) {
          googleSignupUrl.searchParams.set("url", window.location.href);
          window.location.href = googleSignupUrl.toString();
          return;
        }
        toast.error({
          title: i18n._("auth-provider.authentication-failed"),
          description: i18n._("auth-provider.google-authentication-failed"),
        });
      } finally {
        window._msAuthBroadcastChannel?.postMessage("signin");
        markAuthRefresh("login");
        invalidateSession(queryClient);
        setLoginStatus("idle");
      }
    },
    [toast, googleSignupUrl, signOutAsync],
  );

  const signInWithPat = useCallback<LoginContextType["signInWithPat"]>(
    async ({ pat, profile }: { pat: string; profile: string }) => {
      const bearer = `Bearer ${pat}`;
      setLoginStatus("loading");
      // Wipe any leftover Cognito tokens — they may belong to a different user
      // than the one this PAT identifies, and Amplify.configure would otherwise
      // pick them up.
      clearCognitoLocalStorage();
      axiosInstances.authorize(bearer);
      try {
        // Tell the PHP platform we're logged in so it sets its session cookie;
        // the post-login navigation from /auth to /dashboard then carries an
        // authenticated session (PHP doesn't see our Bearer header on a plain
        // navigation, only cookies). This mirrors what session.ts does for the
        // Cognito flow.
        await axiosPost({
          entity: "auth",
          verb: "login",
          token: bearer,
        });
      } catch {
        axiosInstances.authorize("");
        toast.error({
          title: i18n._("auth-provider.authentication-failed"),
          description: i18n._("auth-provider.password-authentication-failed"),
        });
        setLoginStatus("idle");
        return;
      }
      // Persist so the SPA loaded after navigation keeps using `pat:xxx` as
      // its bearer (PHP injects its own Cognito JWT into _msAuth, which is the
      // wrong credential for opaque-auth flows). Leave loginStatus on "loading"
      // — Main.ts is about to fire window.location.assign("/dashboard"), so the
      // page is going away; flipping back to "idle" would just briefly re-render
      // the enabled login form.
      setPatAuthorization(pat);
      setAuthorization(pat);
      setProfile(profile);
    },
    [toast],
  );

  const signIn = useCallback<LoginContextType["signIn"]>(
    async (email, password) => {
      try {
        const result = await send2faAuth({
          email,
          password,
          trust2fa: localStorage.getItem("trust_2fa"),
        });

        if (
          result.status === "invalid-credentials" ||
          result.status === "too-many-attempts"
        ) {
          return result;
        }

        if (result.trust2fa) {
          setLoginStatus("loading");
          await signInMfa(email, password, undefined, result.trust2fa);
          setLastLoginEmail(email);
          window._msAuthBroadcastChannel?.postMessage("signin");
          markAuthRefresh("login");
          invalidateSession(queryClient);
          setLoginStatus("idle");
          return { status: "signed-in" };
        }

        setEmail(email);
        setPassword(password);
        return { status: "code-sent" };
      } catch {
        toast.error({
          title: i18n._("auth-provider.authentication-failed"),
          description: i18n._("auth-provider.password-authentication-failed"),
        });
        signOut({});
        setLoginStatus("idle");
        return { status: "error" };
      }
    },
    [toast, signOutAsync],
  );

  const signInWithPasskey = useCallback<LoginContextType["signInWithPasskey"]>(
    async (passkeyEmail: string) => {
      // Intentionally NOT toggling `loginStatus` here: that unmounts the login
      // form (AuthAction swaps it for a loader), which would discard the form's
      // step state and prevent the fallback to the password step when the
      // passkey attempt fails. The form's own busy flag handles button feedback,
      // and on success the post-login session refetch drives the loader.
      try {
        await completePasskeySignIn({
          email: passkeyEmail,
          userPoolClientId: userPoolsWebClientId,
          userPoolId: userPoolsId,
          userPoolsWebClientId,
        });
        setLastLoginEmail(passkeyEmail);
        window._msAuthBroadcastChannel?.postMessage("signin");
        markAuthRefresh("login");
        invalidateSession(queryClient);
        return "signed-in";
      } catch (error) {
        if (error instanceof PasskeySignInAbortedError) {
          // In-iframe redirect to top is in progress; the page is navigating away.
          return "signed-in";
        }
        if (error instanceof PasskeyNotRegisteredError) {
          // No passkey for this account (or unknown account); fall back to password.
          return "use-password";
        }
        if (
          typeof error === "object" &&
          error !== null &&
          "name" in error &&
          error.name === "NotAllowedError"
        ) {
          // User dismissed the WebAuthn prompt; let them sign in with a password.
          return "use-password";
        }
        signOut({});
        toast.error({
          title: i18n._("auth-provider.authentication-failed"),
          description: i18n._("auth-provider.passkey-authentication-failed"),
        });
        return "error";
      }
    },
    [queryClient, signOut, toast, userPoolsId, userPoolsWebClientId],
  );

  const send2faCode = useCallback<LoginContextType["send2faCode"]>(
    async (code, trust) => {
      let outcome: "signed-in" | "error" = "signed-in";
      try {
        setTrustDevice(trust);
        setLoginStatus("loading");
        await signInMfa(email, password, code);
        if (email) {
          setLastLoginEmail(email);
        }
      } catch {
        outcome = "error";
        toast.error({
          title: i18n._("auth-provider.authentication-failed"),
          description: i18n._("auth-provider.password-authentication-failed"),
        });
        signOut({});
      } finally {
        window._msAuthBroadcastChannel?.postMessage("signin");
        markAuthRefresh("login");
        invalidateSession(queryClient);
        setLoginStatus("idle");
      }
      return outcome;
    },
    [toast, signOutAsync, email, password],
  );

  const { mutateAsync: forgotPassword } = useForgotPassword();

  const { mutateAsync: resetPassword } = useResetPassword();

  useEffect(() => {
    const bc = window._msAuthBroadcastChannel;
    if (!bc) {
      return;
    }
    const listener = (message: MessageEvent) => {
      if (message.data === "signin") {
        markAuthRefresh("login");
        invalidateSession(queryClient);
      }
    };
    bc.addEventListener("message", listener);
    return () => {
      bc.removeEventListener("message", listener);
    };
  }, [queryClient]);

  axiosInstances.useResponseInterceptor(
    "catch-401",
    (response) => response,
    createCatch401ErrorInterceptor(axiosInstances, queryClient),
  );

  useEffect(() => {
    const unregister = axiosInstances.setBearerRefresher(() =>
      refreshSessionBearer({
        queryClient,
        axiosInstances,
        userPoolsWebClientId,
        trustDevice,
      }),
    );
    return unregister;
  }, [axiosInstances, queryClient, userPoolsWebClientId, trustDevice]);

  useEffect(() => {
    const listener = async (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }
      const rawDetail: unknown = event.detail;
      let redirect: string | undefined;
      if (
        typeof rawDetail === "object" &&
        rawDetail !== null &&
        "redirect" in rawDetail &&
        typeof rawDetail.redirect === "string"
      ) {
        redirect = rawDetail.redirect;
      }
      await signOutAsync(redirect ? { redirect } : undefined);
      if (!redirect) {
        markAuthRefresh("login");
        invalidateSession(queryClient);
      }
    };
    window.addEventListener("sign-out", listener);
    return () => {
      window.removeEventListener("sign-out", listener);
    };
  }, [signOutAsync]);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      session: session || null,
      jwt,
      pat: authorization,
      bearer: jwt || `Bearer ${authorization}`,
      isLoggedIn: !!jwt || !!authorization,
      signOut: signOutAsync,
    }),
    [session, jwt, authorization, signOutAsync],
  );

  const signOutAndRefresh = useCallback(async () => {
    await signOutAsync();
    await refreshSessionBearer({
      queryClient,
      axiosInstances,
      userPoolsWebClientId,
      trustDevice: false,
    });
  }, [signOutAsync, queryClient, axiosInstances, userPoolsWebClientId]);

  const loginValue = useMemo<LoginContextType>(
    () => ({
      isBusy: loginStatus === "loading" || isLoading || isFetching,
      email,
      setEmail,
      password,
      googleClientId,
      signIn,
      signInWithPasskey,
      send2faCode,
      signInWithPat,
      googleSignIn,
      forgotPassword,
      resetPassword,
      signOut: signOutAndRefresh,
    }),
    [
      loginStatus,
      isLoading,
      isFetching,
      email,
      password,
      googleClientId,
      signIn,
      signInWithPasskey,
      send2faCode,
      signInWithPat,
      googleSignIn,
      forgotPassword,
      resetPassword,
      signOutAndRefresh,
    ],
  );

  // The router is the child: it stays mounted whatever the session is, and its
  // routes decide between the login screens and the app.
  return (
    <AuthContext value={contextValue}>
      <LoginContext value={loginValue}>{children}</LoginContext>
    </AuthContext>
  );
}

function isGoogleTokenPayload(payload: unknown): payload is { email: string } {
  if (
    typeof payload !== "object" ||
    payload === null ||
    !("email" in payload)
  ) {
    return false;
  }

  const { email } = payload;
  return typeof email === "string";
}

const MemoizedComponent = memo(AuthProvider);
export { MemoizedComponent as AuthProvider };
